import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Badge, Button, Card, CardContent, CardHeader, CardTitle, Input, Modal } from '../../components/ui';

type Call = (name: string, args: Record<string, unknown>) => Promise<unknown>;
type Result = { ok?: boolean; error?: string };
type Prepared = Result & {
  realm: string; alias: string; instance_id: string; tenant_id: string; issuer: string;
  redirect_uri: string; account_url: string; writable: boolean; alias_exists: boolean;
};
type Plan = Result & { plan: { client_id: string; client_secret_present: boolean; idp: string } };

export function EntraOnboardingCard({ call, scopeKey }: { call: Call; scopeKey: string }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const tr = (key: string) => t(`ssoEntra.${key}`);
  const [realm, setRealm] = useState('master');
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({ domain: '', alias: '', display_name: '', client_id: '', client_secret: '', msgraph_slug: '' });
  const [source, setSource] = useState('manual');
  const [prepared, setPrepared] = useState<Prepared | null>(null);
  const [plan, setPlan] = useState<Plan | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const list = useQuery({
    queryKey: ['sso-idp', scopeKey, realm],
    enabled: !!realm,
    queryFn: async () => {
      const r = await call('sso.idp_list', { realm }) as Result & {
        identity_providers: Array<{ alias: string; display_name?: string; issuer?: string; enabled: boolean }>;
      };
      if (!r.ok) throw new Error(r.error || tr('failed'));
      return r.identity_providers;
    },
  });
  const close = () => {
    if (busy) return;
    setOpen(false); setStep(0); setPrepared(null); setPlan(null); setError('');
    setForm((f) => ({ ...f, client_secret: '' }));
  };
  const set = (key: keyof typeof form, value: string) => {
    setForm((f) => ({ ...f, [key]: value })); setError(''); setPlan(null);
  };
  const args = () => ({
    realm, alias: form.alias.trim(), display_name: form.display_name.trim(),
    entra_tenant_id: prepared?.tenant_id,
    ...(source === 'manual' ? { client_id: form.client_id.trim(), client_secret: form.client_secret } : { msgraph_slug: form.msgraph_slug.trim() }),
    create_only: true, expected_instance_id: prepared?.instance_id,
  });
  const run = async (operation: () => Promise<void>) => {
    setBusy(true); setError('');
    try { await operation(); } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  };
  const prepare = () => run(async () => {
    const r = await call('sso.idp_prepare', { realm, alias: form.alias.trim(), entra_tenant_id: form.domain.trim() }) as Prepared;
    if (!r.ok) throw new Error(r.error || tr('failed'));
    if (!r.writable) throw new Error(tr('readOnly'));
    if (r.alias_exists) throw new Error(tr('exists'));
    setPrepared(r); setStep(1);
  });
  const preview = () => run(async () => {
    const r = await call('sso.idp_set', { ...args(), dry_run: true }) as Plan;
    if (!r.ok) throw new Error(r.error || tr('failed'));
    setPlan(r); setStep(2);
  });
  const save = () => run(async () => {
    const r = await call('sso.idp_set', { ...args(), dry_run: false }) as Result;
    if (!r.ok) throw new Error(r.error || tr('failed'));
    setForm((f) => ({ ...f, client_secret: '' })); setStep(3);
    await qc.invalidateQueries({ queryKey: ['sso-idp', scopeKey, realm] });
  });
  const field = (key: keyof typeof form, type = 'text') => (
    <label className='block space-y-1 text-sm'>
      <span>{tr(key)}</span>
      <Input value={form[key]} type={type} autoComplete={type === 'password' ? 'new-password' : 'off'} disabled={busy}
        onChange={(e) => set(key, e.target.value)} />
    </label>
  );
  return <Card>
    <CardHeader><CardTitle>{tr('title')}</CardTitle></CardHeader>
    <CardContent className='space-y-4'>
      <p className='text-sm text-gray-400'>{tr('intro')}</p>
      <div className='flex flex-wrap items-end gap-3'>
        <label className='block space-y-1 text-sm'><span>{tr('realm')}</span>
          <Input value={realm} disabled={open} onChange={(e) => setRealm(e.target.value)} />
        </label>
        <Button disabled={!realm || list.isFetching || !!list.error} onClick={() => { setOpen(true); setCopied(false); }}>{tr('add')}</Button>
      </div>
      {list.isPending && <p role='status'>{tr('loading')}</p>}
      {list.error && <div role='alert' className='text-red-400'>{list.error.message} <Button variant='secondary' onClick={() => list.refetch()}>{tr('retry')}</Button></div>}
      {list.data?.map((p) => <div key={p.alias} className='flex flex-wrap justify-between gap-2 rounded-lg border border-gray-800 p-3 text-sm'>
        <div><div>{p.display_name || p.alias}</div><div className='text-xs text-gray-400 break-all'>{p.alias} · {p.issuer}</div></div>
        <Badge variant={p.enabled ? 'success' : 'warning'}>{tr(p.enabled ? 'enabled' : 'disabled')}</Badge>
      </div>)}
      {list.data?.length === 0 && <p className='text-sm text-gray-500'>{tr('empty')}</p>}
      <Modal open={open} onClose={close} title={tr('title')} size='lg' footer={<>
        <Button variant='ghost' disabled={busy} onClick={close}>{tr(step === 3 ? 'close' : 'cancel')}</Button>
        {step > 0 && step < 3 && <Button variant='secondary' disabled={busy} onClick={() => { setStep(step - 1); setPlan(null); setError(''); }}>{tr('back')}</Button>}
        {step === 0 && <Button loading={busy} disabled={!form.domain.trim() || !form.alias.trim()} onClick={prepare}>{tr('load')}</Button>}
        {step === 1 && <Button loading={busy} disabled={source === 'manual' ? !form.client_id.trim() || !form.client_secret : !form.msgraph_slug.trim()} onClick={preview}>{tr('review')}</Button>}
        {step === 2 && <Button loading={busy} disabled={!plan} onClick={save}>{tr('save')}</Button>}
      </>}>
        <ol className='mb-6 grid grid-cols-2 gap-2 sm:grid-cols-4' aria-label={tr('steps')}>
          {['discover', 'application', 'review', 'login'].map((s, n) => <li key={s} aria-current={step === n ? 'step' : undefined}
            className={`rounded-lg border p-2 text-xs ${step === n ? 'border-blue-500 text-blue-300' : 'border-gray-800 text-gray-500'}`}>{n + 1}. {tr(s)}</li>)}
        </ol>
        <div aria-live='polite'>{busy && <p className='mb-3 text-sm'>{tr('working')}</p>}</div>
        {error && <p role='alert' className='mb-4 rounded-lg border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-300'>{error}</p>}
        {step === 0 && <div className='space-y-4'>
          <p className='text-sm text-gray-400'>{tr('discoverHelp')} <strong>{realm}</strong></p>
          {field('domain')}{field('alias')}{field('display_name')}
          <p className='text-xs text-gray-500'>{tr('aliasHelp')}</p>
        </div>}
        {step === 1 && prepared && <div className='space-y-4'>
          <div className='rounded-lg bg-emerald-500/10 p-3 text-sm'><strong>{tr('discovered')}</strong><div className='break-all font-mono text-xs'>{prepared.tenant_id}</div></div>
          <ol className='list-decimal space-y-2 pl-5 text-sm text-gray-300'>
            <li>{tr('appHelp')} <a className='text-blue-400 underline' href='https://entra.microsoft.com/' target='_blank' rel='noopener noreferrer'>{tr('portal')}</a></li>
            <li>{tr('redirectHelp')}</li><li>{tr('secretHelp')}</li>
          </ol>
          <div className='rounded-lg border border-gray-800 p-3'><div className='text-xs text-gray-400'>{tr('redirect')}</div>
            <code className='block break-all text-sm'>{prepared.redirect_uri}</code>
            <Button variant='secondary' onClick={() => { navigator.clipboard.writeText(prepared.redirect_uri).then(() => setCopied(true)).catch(() => setError(tr('copyFailed'))); }}>{tr(copied ? 'copied' : 'copy')}</Button>
          </div>
          <label className='block space-y-1 text-sm'><span>{tr('source')}</span>
            <select className='acm-field w-full rounded-lg p-2' value={source} disabled={busy} onChange={(e) => { setSource(e.target.value); set('client_secret', ''); }}>
              <option value='manual'>{tr('manual')}</option><option value='msgraph'>{tr('stored')}</option>
            </select>
          </label>
          {source === 'manual' ? <>{field('client_id')}{field('client_secret', 'password')}</> : <>{field('msgraph_slug')}<p className='text-xs text-gray-400'>{tr('storedHelp')}</p></>}
        </div>}
        {step === 2 && prepared && plan && <div className='space-y-4'>
          <dl className='space-y-2 text-sm'>{[[tr('realm'), realm], [tr('alias'), form.alias], [tr('tenant'), prepared.tenant_id], [tr('client_id'), plan.plan.client_id], [tr('redirect'), prepared.redirect_uri]].map(([label, value]) => <div key={label}><dt className='text-gray-400'>{label}</dt><dd className='break-all font-mono'>{value}</dd></div>)}</dl>
          <p className='rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 text-sm text-amber-200'>{tr('reviewHelp')}</p>
        </div>}
        {step === 3 && prepared && <div className='space-y-4'>
          <h3 className='text-lg text-emerald-400'>{tr('saved')}</h3>
          <p className='text-sm text-gray-300'>{tr('loginHelp')}</p>
          <a className='block break-all text-sm text-blue-400 underline' target='_blank' rel='noopener noreferrer' href={prepared.account_url}>{tr('openLogin')}</a>
          <p className='rounded-lg border border-amber-500/30 p-3 text-sm text-amber-200'>{tr('loginPending')}</p>
        </div>}
      </Modal>
    </CardContent>
  </Card>;
}
